import { useMemo, useState } from 'react';
import Modal from './Modal.jsx';
import Avatar from './Avatar.jsx';
import { api } from '../api.js';

const QUICK = ['walking', 'running', 'cycling', 'reading', 'coffee', 'football'];

/** One explicit tap for the tag; place and nearby companions are optional. */
export default function DoingComposer({ me, actors, current, onClose, onSaved, onCleared }) {
  const suggestions = useMemo(
    () => [...new Set([...(me?.interests ?? []), ...QUICK])].slice(0, 12),
    [me?.interests]
  );
  const [tag, setTag] = useState('');
  const [placeName, setPlaceName] = useState('');
  const [companions, setCompanions] = useState([]);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const choose = async (nextTag) => {
    if (!nextTag || busy) return;
    setTag(nextTag);
    // The base case is intentionally the tap itself. Optional fields keep the
    // button below available when someone wants to add context first.
    if (!placeName && companions.length === 0) {
      setBusy(true);
      try {
        const { doing } = await api.createDoing({ tag: nextTag });
        onSaved(doing);
      } catch (err) {
        setError(err.message);
        setBusy(false);
      }
    }
  };

  const submit = async () => {
    const chosen = tag || custom.trim();
    if (!chosen || busy) return;
    setBusy(true);
    setError('');
    try {
      const { doing } = await api.createDoing({
        tag: chosen,
        placeName,
        companionIds: companions,
      });
      onSaved(doing);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="doing-composer">
        <div className="doing-head">
          <Avatar person={me} size="lg" />
          <div>
            <h2>What are you doing?</h2>
            <p>One tap, visible for a few hours. Nothing is added to a location history.</p>
          </div>
        </div>

        {current && (
          <div className="doing-current">
            <span>You’re {current.tag}{current.placeName ? ` at ${current.placeName}` : ''}.</span>
            <button
              type="button" className="btn btn-sm"
              onClick={async () => { await api.clearDoing(); onCleared(); }}
            >
              Clear
            </button>
          </div>
        )}

        <div className="doing-tags">
          {suggestions.map((item) => (
            <button key={item} type="button" className="chip-btn" onClick={() => choose(item)}>
              {item}
            </button>
          ))}
        </div>

        <input
          className="form-input" value={custom} maxLength={30}
          placeholder="Something else…"
          onChange={(event) => { setCustom(event.target.value); setTag(''); }}
        />
        <input
          className="form-input" value={placeName} maxLength={80}
          placeholder="At a public place · optional"
          onChange={(event) => setPlaceName(event.target.value)}
        />

        {actors.length > 0 && (
          <div>
            <div className="field-label">With people here · they confirm first</div>
            <div className="doing-people">
              {actors.slice(0, 12).map((actor) => {
                const on = companions.includes(actor.id);
                return (
                  <button
                    key={actor.id} type="button" className={`companion ${on ? 'on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setCompanions((currentIds) => (
                      on ? currentIds.filter((id) => id !== actor.id) : [...currentIds, actor.id]
                    ))}
                  >
                    <Avatar person={actor} size="xs" /> {actor.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {error && <p className="form-error">{error}</p>}
        {(placeName || companions.length > 0 || custom.trim()) && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? '…' : 'Set doing'}
          </button>
        )}
      </div>
    </Modal>
  );
}
